import * as React from 'react';
import {
    EmptyBox,
    HeaderListSkeleton,
    LazyBreadcrumb,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import {
    DossierOpenSearch,
    DossierOpenSearchSelectorMain,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    selectedPrescripteursSelector,
    useFormaliteService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import {
    DossierActionContext,
    dossiersBasePath,
    DossierTable,
    PossibleTypes,
    useSearchBar,
    DossierDocumentsProvider,
} from '@europrocurement/l2d-modules';

import { DataSource } from '@europrocurement/l2d-redux-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { AnyAction } from '@reduxjs/toolkit';
import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { Box } from '@mui/material';
import { useCurrentContact } from '@europrocurement/l2d-modules/hooks/useCurrentContact';
import { useDossierTable } from '../../hooks';

const ListeDossiers: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const datasource: DataSource<DossierOpenSearch> = useSelector(DossierOpenSearchSelectorMain);
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const { getOpenSearchDossiers } = useFormaliteService();
    const { dossiersUpdated } = React.useContext(DossierActionContext);
    const { navigateOnClick } = useClickActions({ navigate });

    const { columns, filters, preFilters, rowActions } = useDossierTable();
    const [filtersInitialized, setFiltersInitialized] = React.useState<boolean>(false);

    const { getCurrentContactByUserIdentifier } = useCurrentContact();
    const currContact = getCurrentContactByUserIdentifier();

    const breadcrumbItems = [
        {
            link: '/accueil',
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: { display: 'flex', alignItems: 'center' },
            },
        },
        {
            link: '/dossiers',
            path: 'Mes dossiers',
        },
        'Liste de mes dossiers',
    ];

    // Activation du flag de recherche de dossiers de formalité
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityFolio);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        if (dossiersUpdated.length) {
            setTimeout(() => {
                dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
            }, 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiersUpdated]);

    React.useEffect(() => {
        if (!filtersInitialized) {
            // Attendre que les données soient chargées avant d'initialiser les filtres
            if (currContact === undefined || prescripteur === undefined) {
                return;
            }

            console.log(
                'Initialisation des filtres de la liste des dossiers avec contactId :',
                currContact?.id,
                'et prescriberId :',
                prescripteur?.id,
            );

            if (
                datasource.filters.contactId === undefined &&
                currContact?.id !== undefined &&
                currContact?.id !== null
            ) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                    payload: { key: 'contactId', value: currContact.id },
                });
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                    payload: {
                        key: 'contactId',
                        label: `${currContact.prenomContact} ${currContact.nomContact?.toUpperCase()}`,
                    },
                });
            }
            if (prescripteur?.id !== undefined && prescripteur?.id !== null) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                    payload: {
                        key: 'prescriberId',
                        value: prescripteur?.id,
                    },
                });
            }
        }
        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
        setFiltersInitialized(true);
    }, [
        currContact,
        datasource.filters.contactId,
        dispatch,
        filtersInitialized,
        getOpenSearchDossiers,
        prescripteur,
    ]);

    const handleRowClick = React.useCallback(
        ({ id }: DossierOpenSearch) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        ({ id }: DossierOpenSearch) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    // Attendre l'initialisation des filtres avant d'afficher quoi que ce soit
    if (!filtersInitialized) {
        return <ListSkeleton columns={6} />;
    }

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <DossierDocumentsProvider // Prevent error on useTable - useDossierActionMenuItems that need the documents for back actions
                autoload={false}
                dossierIds={[]}
            >
                <LazyBreadcrumb
                    path={breadcrumbItems}
                    separator=" / "
                    sx={{ marginBottom: '1.5em' }}
                />
                <DossierTable
                    datasource={datasource}
                    columns={columns}
                    filters={filters}
                    preFilters={preFilters}
                    rowActions={rowActions}
                    onRowClick={handleRowClick}
                    onWheelClick={handleRowWheelClick}
                />
            </DossierDocumentsProvider>
        </SkeletonLoader>
    );
};

export default ListeDossiers;
